import React, { useEffect, useState } from "react";
import { handleApiError } from "@/utils/toastUtil";

import { Eye, EyeOff, Lock } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import api from "../../api";
import LanguageSelector from "../LanguageSelector";
import { BASE_URL } from "../../config";

export function ResetPassword() {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [token, setToken] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get("token");
    setToken(tokenFromUrl);
  }, [location]);

  const handleResetPassword = async (password, token) => {
    try {
      const response = await api.post(
        `${BASE_URL}/auth/reset-password`,
        { newPassword: password, token },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        return true;
      }
    } catch (error) {
      if (error.response?.status === 404) {
        return false;
      }
      console.error(t("resetPassword.error.generic"), error);
      handleApiError("resetPassword.error.generic");
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError(t("resetPassword.error.mismatch"));
      return;
    }

    setIsLoading(true);
    try {
      await handleResetPassword(password, token);
      setIsSuccess(true);
    } catch (err) {
      setError(t("resetPassword.error.failed"));
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-subMain flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-md">
          <div className="bg-white py-8 px-6 sm:px-10 shadow sm:rounded-lg border border-border">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-main mb-4">
                {t("resetPassword.success.title")}
              </h2>
              <p className="text-textGray mb-6">
                {t("resetPassword.success.message")}
              </p>
              <a
                href="/"
                className="text-subMain hover:text-subMain-dark transition">
                {t("resetPassword.success.backToLogin")}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-subMain min-h-screen flex flex-col justify-center px-4 sm:px-6 lg:px-8 relative">
      <div className="absolute top-1 right-1 md:right-5">
        <LanguageSelector />
      </div>
      <div className="bg-subMain flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="mt-8 mx-auto w-full max-w-md">
          <div className="bg-white py-8 px-6 sm:px-10  shadow rounded-lg border border-border">
            <div className="mx-auto w-full max-w-md mb-10">
              <div className="flex justify-center">
                <Lock className="h-12 w-12 text-subMain" />
              </div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-main">
                {t("resetPassword.title")}
              </h2>
            </div>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-main">
                  {t("resetPassword.fields.newPassword")}
                </label>
                <div className="mt-1 relative" dir="ltr">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-border rounded-md shadow-sm placeholder-textGray focus:outline-none focus:ring-subMain focus:border-subMain"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-textGray" />
                    ) : (
                      <Eye className="h-5 w-5 text-textGray" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-main">
                  {t("resetPassword.fields.confirmPassword")}
                </label>
                <div className="mt-1" dir="ltr">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-border rounded-md shadow-sm placeholder-textGray focus:outline-none focus:ring-subMain focus:border-subMain"
                  />
                </div>
              </div>

              {error && <div className="text-red-600 text-sm">{error}</div>}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-subMain hover:bg-subMain-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-subMain disabled:opacity-50 disabled:cursor-not-allowed">
                  {isLoading
                    ? t("resetPassword.loading")
                    : t("resetPassword.submit")}
                </button>
                <div className="mt-6">
                  <div className="relative">
                    <div className="relative flex justify-center text-sm">
                      <a
                        href="/"
                        className="text-subMain hover:text-subMain-dark transition">
                        {t("resetPassword.backToLogin")}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
